import { isBusy, type Job, type Sheet } from './model';

/**
 * A job's sheets, the one that last changed first.
 *
 * The sheet just photographed or just approved is what the operator is looking
 * for, and the server returns them in id order. Ties go to the higher id.
 */
export function sortSheets(sheets: Sheet[]): Sheet[] {
  return [...sheets].sort((a, b) =>
    b.updatedAt.localeCompare(a.updatedAt) || b.sheetId.localeCompare(a.sheetId));
}

/**
 * Sheets a photo has just been sent for, each with the `updatedAt` it had
 * before the photo went, kept until the pre-check picks the photo up and
 * changes it.
 *
 * The page polls only while a sheet is busy, and sending a photo does not make
 * it busy: the upload leaves the sheet alone, and the pre-check moves it to
 * `prechecking` about 1.5 s later, after the S3 event and its own start. So the
 * one load after an upload usually saw `awaiting_photo` and polled no further.
 * From 2026-10-22 to 10-27 half the sheets sat unchanged on screen until the
 * page was reopened, which is the "takes closer to a minute" of SLD-0006.
 */
export type Expected = Record<string, { since: string; sentAt: number }>;

/** How long to wait for the pre-check to pick a photo up before giving up. */
export const EXPECT_MS = 60_000;

/**
 * A photo on its way to `sheetId`, waited for until the sheet changes from
 * `since`. `now` starts the EXPECT_MS clock, so it is reset once the photo is
 * in: a 48 MP photo on shop Wi-Fi can take most of a minute to upload.
 */
export function expectPhoto(expected: Expected, sheetId: string, since: string, now: number)
  : Expected {
  return { ...expected, [sheetId]: { since, sentAt: now } };
}

/** What is still on its way: not yet picked up, and not given up on. */
export function stillExpected(expected: Expected, sheets: Sheet[], now: number): Expected {
  const out: Expected = {};
  for (const [id, e] of Object.entries(expected)) {
    const sheet = sheets.find((s) => s.sheetId === id);
    if (sheet && sheet.updatedAt === e.since && now - e.sentAt < EXPECT_MS) out[id] = e;
  }
  return out;
}

/**
 * Sheets that finished between two loads: busy, or with a photo on its way,
 * in the first, and ready to review or refused in the second. A sheet that was
 * already finished when the page opened is not news.
 */
export function finished(before: Sheet[], after: Sheet[], expected: Expected)
  : { ready: Sheet[]; refused: Sheet[] } {
  const ready: Sheet[] = [];
  const refused: Sheet[] = [];
  for (const sheet of after) {
    const was = before.find((s) => s.sheetId === sheet.sheetId);
    if (!was || was.updatedAt === sheet.updatedAt) continue;
    // Changed only by the upload's own write: a reshot refusal is still the
    // old one until the pre-check has been.
    const sent = expected[sheet.sheetId];
    if (!isBusy(was.state) && (!sent || sent.since === sheet.updatedAt)) continue;
    if (sheet.state === 'needs_review') ready.push(sheet);
    else if (sheet.state === 'refused' || sheet.state === 'error') refused.push(sheet);
  }
  return { ready, refused };
}

/**
 * The jobs list, split into what is in the way and what has been put aside.
 *
 * Archiving hides a job from the list and does nothing else -- no sheet, no
 * photograph and no report is touched. It exists because the list only grows:
 * the client adds a job per design and never has a reason to remove one, so
 * after a season the jobs that matter are below the fold.
 *
 * Both halves come from the same fetch rather than from a second request with
 * a filter. `list-jobs` already scans the whole table on the grounds that one
 * shop accumulates jobs at the rate someone can tape canvas to a bed, so the
 * archived ones are already in hand, and showing them is a toggle rather than
 * a round trip. It also means an archived job can never become unreachable
 * because of a query someone forgot to write.
 */
export function partitionJobs(jobs: Job[]): { active: Job[]; archived: Job[] } {
  const active: Job[] = [];
  const archived: Job[] = [];
  for (const job of jobs) (job.archivedAt ? archived : active).push(job);
  return { active, archived };
}

/**
 * What the jobs screen shows, given the URL it was opened with.
 *
 * The archive is offered, collapsed, whenever there is one (SLD-0004). It used
 * to be hidden outright unless the URL carried `?archived=1`, after a client
 * visit where "Show archived (7)" announced somebody else's test runs -- but
 * then nobody could find an archived job at all. What is kept from that rule:
 * nothing about the archive appears when it is empty, and the list itself
 * stays closed until asked for. `?archived=1` now just opens it.
 *
 * Hiding never removes: every row keeps its `archivedAt`, no sheet or
 * photograph is touched, and a job page still opens by its own URL.
 */
export function jobsView(jobs: Job[], search: string) {
  const { active, archived } = partitionJobs(jobs);
  return {
    active,
    archived,
    archiveOpenAtStart: new URLSearchParams(search).has('archived'),
    emptyMessage:
      active.length > 0 ? null
        : archived.length > 0 ? 'Every job is archived.'
          : 'No jobs yet.',
  };
}

/**
 * Where a multi-select stands against the jobs it can pick from, for the
 * "select all" box: none, some (drawn indeterminate) or all.
 *
 * Only ids still in the list count, so a job archived or restored since it was
 * ticked cannot leave the box claiming more than the screen shows.
 */
export function selectionState(
  ids: readonly string[], selected: ReadonlySet<string>,
): { count: number; all: boolean; some: boolean } {
  const count = ids.filter((id) => selected.has(id)).length;
  return { count, all: ids.length > 0 && count === ids.length, some: count > 0 && count < ids.length };
}

/**
 * What a new job is called: the name typed, or the design's filename without
 * its `.pdf` (SLD-0004). A job is one file, and the file is usually already
 * called what the job is, so typing it again was a step with no information
 * in it. The same rule runs on the server (`api/src/create-job.ts`).
 */
export function jobNameFrom(name: string, filename: string): string {
  const given = name.trim();
  if (given) return given.slice(0, 60);
  return filename.replace(/\.pdf$/i, '').trim().slice(0, 60);
}

/**
 * Does this job still need its thumbnail drawn? True for a job with a design
 * and no thumbnail, which is every job from before thumbnails existed; the
 * job page draws one from the design the first time it is opened.
 */
export function needsThumbnail(job: Pick<Job, 'artworkUrl' | 'thumbnailUrl'>): boolean {
  return Boolean(job.artworkUrl) && !job.thumbnailUrl;
}
